<script>
import Base from './base.vue';
import { mapGetters } from 'vuex';
import { computedCallingPlans, computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import {
  ZOOM_MEETING_PRO_MIN_LICENSE,
  BILLING_CYCLE,
  contactUsPath,
  PlanDisabledStatusEnum,
  planTypes,
} from '@/common/constants';
import autoAdd from '../mixin/auto-add';
import { checkBizPlusAddonLicense, isInUnlockBizPlusCountRetentionFlow } from '../utils';
import { dateTimeFormat } from '@/utils/dateformat.js';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';

// validDownSellLicense
import { validDownSellLicenseForBizPlus } from '@/utils/validate-downsell';
import plansMixin from '@/plans/mx/plan';
import { plansof } from '@/utils/chain';
import { bundleRegions, zoneBundleMode } from '../helper';
import { subscriptionLevelActionEnum } from '@/views/config/coupon/helper/constant';

export default {
  name: 'EDIT',
  extends: Base,
  mixins: [autoAdd, plansMixin],
  data() {
    return {
      isExpanded: true,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'basicConfig', 'getUserCurrentSubscriptions', 'couponSubscriptionLevelAction']),
    showCycle () {
      return false;
    },
    pbxRegions() {
      return bundleRegions.filter((x) => {
        // for legacy customer who has been bought UK region
        const hasBought = this.currentBoughtBizPlusPlans.find(p => p.region === x.key);
        if (hasBought?.isFreeTrial) return false;
        if (x.key === PBX_REGIONS.JP){
          return (!this.$config('hideZoomPhoneJPPlans') && !!hasBought && !this.$config('hideZoomPhoneJPPlansPhase2')) || this.$config('enableBizPlusJapanOnlineBuy');
        }
        if (this.$config('hideZoomPhoneUKIRPlans')) {
          if (hasBought) return true;
          return x.key !== PBX_REGIONS.UK_AND_IRELAND;
        }
        return true;
      })
      .filter(x => this.bundlePlans.find(item => item.region === x.key))
      .filter(zoneBundleMode.filter);
    },
    planBillInfo() {
      const plan = computedCallingPlans(this.boughtPlans)?.[0] ?? {};
      const basePlan = computedPbxBasePlans(this.boughtPlans)?.[0] ?? {};
      const payment = getZPCallingFeesText(basePlan).title;
      const billingCycle = basePlan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO ? BILLING_CYCLE.MONTHLY : plan.billing_cycle;
      return {
        billing_cycle: billingCycle,
        payment
      }
    },
    planDetailLabel() {
      if (this.isBulkLicenseAccount || this.fromCampaignLink) return '';
      return this.$t('billing.zone.explore_other_plans_edit');
    },
    callingPlanOptions() {
      return this.pbxRegions.map((x) => {
        const hasBought = this.currentBoughtBizPlusPlans.find(p => p.region === x.key);
        let min = 1;
        if (hasBought && this.disableDownSell) {
          min = hasBought.cartInfo.selected_number;
        }

        if (hasBought && this.forNonStandardPriceTips && hasBought.isStandardPrice === false) {
          min = hasBought.cartInfo.selected_number;
        }

        if (hasBought && (this.fromCampaignLink || this.isBulkLicenseAccount)) {
          if (this.plansofBizBundle.totalZOLicenses <= this.minLicense) {
            // biz + biz plus >= this.minLicense
            const selectedPlan = this.currentSelectedBizPlusPlans.find(p => p.region === x.key);
            if (selectedPlan) {
              min = selectedPlan.cartInfo.selected_number;
            }
          }
        }

        min = min >= 1 ? min : 1;

        return ({
          id: x.key,
          name: this.$t(x.label),
          desc: '',
          min: min,
          max: 9999,
        })
      });
    },
    showAddAnotherRegionCTA() {
      const showAddAnotherRegionCTA = this.callingPlanRegions.length !== this.callingPlanOptions.length;
      // can only edit one reginonal plan at the same time for bk account
      if (this.isBulkLicenseAccount) {
        // if has free trial plan, filter it
        const boughtPlansWithoutFreeTrial = this.currentBoughtBizPlusPlans.filter(p => !p.isFreeTrial);
        if (boughtPlansWithoutFreeTrial.length >= 1 || boughtPlansWithoutFreeTrial.length === this.pbxRegions.length) {
          return false;
        }
      }
      if (this.fromCampaignLink) return false;
      return showAddAnotherRegionCTA;
    },
    currentBoughtBizPlusPlans() {
      return this.plansofBizBundle.boughtBizPlusPlan;
    },
    currentSelectedBizPlusPlans() {
      return this.plansofBizBundle.selectedBizPlusPlan;
    },
    disableDownSell() {
      return this?.basicConfig?.downSellData?.disableDownSell;
    },
    licenseTips() {
      const boughtMap = this.currentBoughtBizPlusPlans.reduce((prev, cur) => {
        prev[cur.plan_code] = cur.cartInfo.selected_number;
        return prev;
      }, {});
      const selectedMap = this.currentSelectedBizPlusPlans.reduce((prev, cur) => {
        prev[cur.plan_code] = cur.cartInfo.selected_number;
        return prev;
      }, {});
      // add-on tips
      if (this.bizPlusAddonLicenseTips.message) {
        return this.bizPlusAddonLicenseTips.message;
      }
      // assign info
      let needAssignTotalQuantity = 0;
      let expireTime = null;
      this.currentSelectedBizPlusPlans.forEach(p => {
        const { needAssignQuantity, planExpireTime } = validDownSellLicenseForBizPlus({ quantity: p.cartInfo.selected_number, planCode: p.plan_code });
        if (needAssignQuantity > 0) {
          needAssignTotalQuantity += needAssignQuantity;
        }
        if (!expireTime) {
          expireTime = planExpireTime;
        }
      });
      if (needAssignTotalQuantity > 0) {
        return expireTime
          ? this.$t('billing.error.downgrade_alert_info', [needAssignTotalQuantity, dateTimeFormat(expireTime)])
          : this.$t('billing.error.downgrade_alert_info_no_date', [needAssignTotalQuantity])
      }
      let hasSameQuantity = false;
      Object.keys(selectedMap).forEach(key => {
        if (boughtMap[key] === selectedMap[key]) {
          hasSameQuantity = true;
        }
      });
      if (hasSameQuantity) {
        // MRR >= 500 (direct account)
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }

      if (this.fromCampaignLink) {
        if (this.plansofBizBundle.totalZOLicenses <= this.minLicense) {
          const maxLicenseAddon = this.plansofBizBundle.maxLicensesAddonPlan;
          console.log(maxLicenseAddon);
          if (maxLicenseAddon?.cartInfo?.selected_number >= ZOOM_MEETING_PRO_MIN_LICENSE) {
            return this.$t('billing.meeting_license_decrease_tips_need_decrease_addon', {
              product: this.$t(maxLicenseAddon.typeNameKey),
            });
          }
          return this.$t('billing.zobizplus.required_license_alert', [this.minLicense]);
        }
      }

      return '';
    },
    minLicense() {
      // webinar, large meeting, zoom session/events
      const min = this.bizPlusAddonLicenseTips.min;
      return min >= ZOOM_MEETING_PRO_MIN_LICENSE ? min : ZOOM_MEETING_PRO_MIN_LICENSE;
    },
    bizPlusAddonLicenseTips() {
      return checkBizPlusAddonLicense();
    },
    isStandardPrice() {
      const isStandardPrice = this.currentSelectedBizPlusPlans.find(p => p.isStandardPrice === false);
      if (isStandardPrice) {
        return (!this.hasAccountDiscount) ? false : true;
      }
      return true;
    },
    forNonStandardPriceTips() {
      return !this.isStandardPrice
        ? this.$t('billing.planform.meeting_license_custom_pricing')
        : '';
    },
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount;
    },
    hasBoughtJPPbxPlans(){
      return !! (this.boughtPlans?.filter?.(item => item.region === PBX_REGIONS.JP)?.length)
    }
  },
  watch: {
    currentSelectedBizPlusPlans: {
      handler() {
        this.mx_initCallPlanRegions();
      },
    },
  },
  methods: {
    async handlePlanDetailsClick() {
      await this.showFullComparisonDialog();
    },
    mx_initCallPlanRegions() {
      const zobundlePlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      // sort by region when click add another region button
      const regionOrder = this.callingPlanRegions.map(p => p.id).filter(p => p);
      const sortRegionsPlan = zobundlePlans.sort((a,b) => {
        const indexA = regionOrder.indexOf(a.region);
        const indexB = regionOrder.indexOf(b.region);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      this.callingPlanRegions = sortRegionsPlan.map((x) => {
        const { disabled, tooltip, shouldDisabled, disableDelete, disableSwitch, deleteTips } = this.mx_getCallingPlanDisable(x);
        return {
          id: x.region,
          number: x.cartInfo?.selected_number,
          plan_code: x.plan_code,
          disabled: disabled,
          inputTooltip: tooltip,
          shouldDisabled,
          disableSwitch,
          disableDelete,
          deleteTips
        }
      });
    },
    mx_getCallingPlanDisable(plan) {
      const hasBought = this.currentBoughtBizPlusPlans.find(p => p.plan_code === plan.plan_code);
      const isDirectSales = this.basicConfig?.isDirectSales
      if (hasBought && this.isBulkLicenseAccount) {
        return {
          disabled: false,
          tooltip: this.$t('billing.error.buld_acount_license'),
          shouldDisabled: true
        }
      }
      if (hasBought && this.disableDownSell) {
        return {
          disabled: false,
          tooltip: '',
          shouldDisabled: true
        }
      }
      if (hasBought && this.fromCampaignLink) {
        return {
          shouldDisabled: true,
          disabled: false,
        };
      }

      // for universal coupon
      if (this.fromCampaignLink && subscriptionLevelActionEnum.isUniversalCouponSubscription(this.couponSubscriptionLevelAction)) {
        return {
          shouldDisabled: true,
          disabled: false,
        }
      }

      const disableReason = this.plancodeMapSubscriptionDisabled[plan.plan_code]
      if (disableReason) {
        if (isInUnlockBizPlusCountRetentionFlow() && disableReason === PlanDisabledStatusEnum.CANCELED) {
          return {
            disabled: false,
            tooltip: ''
          }
        } else {
          return {
            disabled: true,
            tooltip: this.$t('billing.zobizplus.pending_change')
          };
        }
      }
      if ((isDirectSales && hasBought) || hasBought?.ratePlans?.length > 1) {
        return {
          disabled: false,
          tooltips: '',
          disableSwitch: true,
          disableDelete: true,
          deleteTips: this.$t('billing.cancel_disable_contact_us', {
            url: contactUsPath
          }),
        }
      }
      return {
        disabled: false,
        tooltip: ''
      }
    },
  },
};
</script>
